/**
 * Las lecturas de la API, una función por endpoint.
 *
 * Son las mismas que tenía el módulo de servidor de Next, con la misma firma:
 * lo único que cambió es quién las llama. Antes las invocaba un componente de
 * servidor y ahora un `useQuery`, así que siguen siendo funciones que devuelven
 * una promesa y no saben nada de React.
 *
 * Las mutaciones viven en cada módulo, al lado de la pantalla que las usa.
 */
import { apiFetch, searchSuffix } from "@/lib/api/client";
import type {
  AgendaSummary,
  AgentDetail,
  AgentConfig,
  AgentDocument,
  AgentSummary,
  AgentVersion,
  AnalyticsOverview,
  AssignmentRule,
  CalendarConnection,
  CalendarEvent,
  CampaignRecipient,
  CampaignSummary,
  Channel,
  Contact,
  ExternalToolList,
  ContactCounts,
  ContactPage,
  ContactQuery,
  Conversation,
  ConversationEvent,
  ConversationPage,
  DetectedSites,
  DocumentFormats,
  EditableField,
  InboxMeta,
  InboxQuery,
  Inquiry,
  InquiryKind,
  InboundEmail,
  InboundEmailPayload,
  InboundEmailSettings,
  Investment,
  MissingProducers,
  LeadSourcePayload,
  LeadSourcesConfig,
  Navigation,
  Note,
  PipelineConfig,
  PropertyFacets,
  SiteSettings,
  PropertyPage,
  PropertyQuery,
  Segment,
  SegmentVocabulary,
  SessionUser,
  Task,
  Turn,
  Visit,
  ChannelTemplates,
  StandardTemplate,
  UsageReport,
  WhatsAppTemplate,
} from "@/lib/api/types";

/* --- Sesión y navegación --- */

export function getSessionUser(): Promise<SessionUser> {
  return apiFetch<SessionUser>("/api/v1/me");
}

export function getNavigation(): Promise<Navigation> {
  return apiFetch<Navigation>("/api/v1/me/navigation");
}

/* --- Agentes --- */

export function listAgents(): Promise<AgentSummary[]> {
  return apiFetch<AgentSummary[]>("/api/v1/agents");
}

export function getAgent(agentId: number): Promise<AgentDetail> {
  return apiFetch<AgentDetail>(`/api/v1/agents/${agentId}`);
}

export function listAgentVersions(agentId: number): Promise<AgentVersion[]> {
  return apiFetch<AgentVersion[]>(`/api/v1/agents/${agentId}/versions`);
}

/** La configuración de la versión publicada. Solo existe si alguna vez se publicó. */
export function getPublishedConfig(agentId: number): Promise<AgentConfig> {
  return apiFetch<AgentConfig>(`/api/v1/agents/${agentId}/published-config`);
}

/** Viven aparte del borrador: tienen su propio CRUD y se congelan al publicar. */
export function listExternalTools(agentId: number): Promise<ExternalToolList> {
  return apiFetch<ExternalToolList>(`/api/v1/agents/${agentId}/external-tools`);
}

export function listChannels(): Promise<Channel[]> {
  return apiFetch<Channel[]>("/api/v1/channels");
}

/** Consultas abiertas de fichas cuyo productor no tiene cuenta en Clara (owner y admin). */
export function getMissingProducers(): Promise<MissingProducers> {
  return apiFetch<MissingProducers>("/api/v1/listing-agents/missing-producers");
}

/**
 * Las plantillas aprobadas de un canal de WhatsApp.
 *
 * Fuera de la ventana de 24 horas es lo único que Meta deja mandar, así que sin
 * esto un seguimiento a los dos días no existe.
 */
export function listTemplates(channelId: number): Promise<WhatsAppTemplate[]> {
  return apiFetch<WhatsAppTemplate[]>(`/api/v1/channels/${channelId}/templates`);
}

export function getUsage(): Promise<UsageReport> {
  return apiFetch<UsageReport>("/api/v1/usage");
}

export function listApprovedTemplates(): Promise<ChannelTemplates[]> {
  return apiFetch<ChannelTemplates[]>("/api/v1/whatsapp/approved-templates");
}

export function listStandardTemplates(channelId: number): Promise<StandardTemplate[]> {
  return apiFetch<StandardTemplate[]>(`/api/v1/channels/${channelId}/templates/standard`);
}

/* --- Bandeja ---
 * Los filtros viajan en la URL y no en estado del cliente: así una vista
 * filtrada se puede compartir, recargar y volver atrás con el navegador. */

export function listConversations(query: InboxQuery = {}): Promise<ConversationPage> {
  return apiFetch<ConversationPage>(`/api/v1/conversations${searchSuffix(query)}`);
}

/** Las etapas y los conteos del embudo pedido; sin `kind`, el de compradores. */
export function getInboxMeta(kind?: InquiryKind): Promise<InboxMeta> {
  return apiFetch<InboxMeta>(`/api/v1/conversations/meta${searchSuffix({ kind })}`);
}

export function getUnreadCount(): Promise<{ count: number }> {
  return apiFetch<{ count: number }>("/api/v1/conversations/unread");
}

export function markConversationRead(conversationId: number): Promise<void> {
  return apiFetch<void>(`/api/v1/conversations/${conversationId}/read`, { method: "POST" });
}

export function getConversation(conversationId: number): Promise<Conversation> {
  return apiFetch<Conversation>(`/api/v1/conversations/${conversationId}`);
}

export function listTurns(conversationId: number): Promise<Turn[]> {
  return apiFetch<Turn[]>(`/api/v1/conversations/${conversationId}/messages`);
}

export function listConversationEvents(conversationId: number): Promise<ConversationEvent[]> {
  return apiFetch<ConversationEvent[]>(`/api/v1/conversations/${conversationId}/events`);
}

export function listNotes(conversationId: number): Promise<Note[]> {
  return apiFetch<Note[]>(`/api/v1/conversations/${conversationId}/notes`);
}

export function listEditableFields(): Promise<EditableField[]> {
  return apiFetch<EditableField[]>("/api/v1/conversations/editable-fields");
}

/* --- Pipeline y contactos --- */

export function getPipeline(): Promise<PipelineConfig> {
  return apiFetch<PipelineConfig>("/api/v1/pipeline");
}

/** El embudo de un tipo de consulta: el de propietarios tiene otras etapas. */
export function getPipelineFor(kind: "buyer" | "seller"): Promise<PipelineConfig> {
  return apiFetch<PipelineConfig>(`/api/v1/pipeline?kind=${kind}`);
}

/* --- Fuentes de leads --- */

export function getLeadSources(): Promise<LeadSourcesConfig> {
  return apiFetch<LeadSourcesConfig>("/api/v1/settings/lead-sources");
}

/**
 * Reemplaza el catálogo entero: la posición es el orden de la lista.
 *
 * Vive acá y no en una mutación del módulo porque es la única escritura de la
 * pantalla, y así el test la ve por la misma puerta que la lectura.
 */
export function putLeadSources(items: LeadSourcePayload[]): Promise<LeadSourcesConfig> {
  return apiFetch<LeadSourcesConfig>("/api/v1/settings/lead-sources", {
    method: "PUT",
    body: { items },
  });
}

/* --- Tu web --- */

export function getSites(): Promise<SiteSettings> {
  return apiFetch<SiteSettings>("/api/v1/settings/sites");
}

export function getDetectedSites(): Promise<DetectedSites> {
  return apiFetch<DetectedSites>("/api/v1/settings/sites/detected");
}

/** Reemplaza los dominios agregados; el backend los devuelve normalizados. */
export function putSites(ownDomains: string[]): Promise<SiteSettings> {
  return apiFetch<SiteSettings>("/api/v1/settings/sites", {
    method: "PUT",
    body: { own_domains: ownDomains },
  });
}

/* --- Consultas por email --- */

export function getInboundEmailSettings(): Promise<InboundEmailSettings> {
  return apiFetch<InboundEmailSettings>("/api/v1/settings/inbound-email");
}

export function putInboundEmailSettings(
  payload: InboundEmailPayload,
): Promise<InboundEmailSettings> {
  return apiFetch<InboundEmailSettings>("/api/v1/settings/inbound-email", {
    method: "PUT",
    body: payload,
  });
}

/** Una dirección nueva: la anterior deja de recibir en ese mismo momento. */
export function regenerateInboundEmailAddress(): Promise<InboundEmailSettings> {
  return apiFetch<InboundEmailSettings>("/api/v1/settings/inbound-email/regenerate", {
    method: "POST",
  });
}

/** Vuelve a encolar un correo que falló o no se reconoció. 409 si ya no tiene cuerpo. */
export function reprocessInboundEmail(emailId: number): Promise<InboundEmail> {
  return apiFetch<InboundEmail>(
    `/api/v1/settings/inbound-email/messages/${emailId}/reprocess`,
    { method: "POST" },
  );
}

/** Crea la consulta de un correo que el clasificador dejó en duda. 409 si ya no se puede. */
export function confirmInboundEmail(emailId: number): Promise<InboundEmail> {
  return apiFetch<InboundEmail>(`/api/v1/settings/inbound-email/messages/${emailId}/confirm`, {
    method: "POST",
  });
}

export function listInboundEmails(limit = 20): Promise<InboundEmail[]> {
  return apiFetch<{ items: InboundEmail[] }>(
    `/api/v1/settings/inbound-email/messages${searchSuffix({ limit })}`,
  ).then((page) => page.items);
}

export function listContacts(query: ContactQuery = {}): Promise<ContactPage> {
  return apiFetch<ContactPage>(`/api/v1/contacts${searchSuffix(query)}`);
}

export function getContactCounts(): Promise<ContactCounts> {
  return apiFetch<ContactCounts>("/api/v1/contacts/counts");
}

export function getContact(contactId: number): Promise<Contact> {
  return apiFetch<Contact>(`/api/v1/contacts/${contactId}`);
}

/** Las consultas de un contacto, de la más reciente a la más vieja: el backend ya las ordena. */
export function getContactInquiries(contactId: number): Promise<Inquiry[]> {
  return apiFetch<Inquiry[]>(`/api/v1/contacts/${contactId}/inquiries`);
}

/* --- Agenda --- */

export function getAgendaSummary(): Promise<AgendaSummary> {
  return apiFetch<AgendaSummary>("/api/v1/agenda");
}

export function listVisits(
  params: { conversationId?: number; onlyOpen?: boolean } = {},
): Promise<Visit[]> {
  return apiFetch<Visit[]>(
    `/api/v1/visits${searchSuffix({
      conversation_id: params.conversationId,
      only_open: params.onlyOpen,
    })}`,
  );
}

export function listTasks(
  params: { conversationId?: number; includeCompleted?: boolean } = {},
): Promise<Task[]> {
  return apiFetch<Task[]>(
    `/api/v1/tasks${searchSuffix({
      conversation_id: params.conversationId,
      include_completed: params.includeCompleted,
    })}`,
  );
}

/* --- Calendario --- */

export function getCalendarStatus(): Promise<CalendarConnection> {
  return apiFetch<CalendarConnection>("/api/v1/calendar/status");
}

export function listCalendarEvents(days = 7): Promise<CalendarEvent[]> {
  return apiFetch<CalendarEvent[]>(`/api/v1/calendar/events?days=${days}`);
}

/* --- Catálogo --- */

export function searchProperties(query: PropertyQuery = {}): Promise<PropertyPage> {
  return apiFetch<PropertyPage>(`/api/v1/properties${searchSuffix(query)}`);
}

export function getPropertyFacets(): Promise<PropertyFacets> {
  return apiFetch<PropertyFacets>("/api/v1/properties/facets");
}

/* --- Analytics --- */

export function getAnalyticsOverview(days: number): Promise<AnalyticsOverview> {
  return apiFetch<AnalyticsOverview>(`/api/v1/analytics/overview?days=${days}`);
}

export function listInvestments(): Promise<Investment[]> {
  return apiFetch<Investment[]>("/api/v1/analytics/investments");
}

/* --- Segmentos y campañas --- */

export function getSegmentVocabulary(): Promise<SegmentVocabulary> {
  return apiFetch<SegmentVocabulary>("/api/v1/segments/vocabulary");
}

export function listSegments(): Promise<Segment[]> {
  return apiFetch<Segment[]>("/api/v1/segments");
}

export function getSegment(segmentId: number): Promise<Segment> {
  return apiFetch<Segment>(`/api/v1/segments/${segmentId}`);
}

export function listCampaigns(): Promise<CampaignSummary[]> {
  return apiFetch<CampaignSummary[]>("/api/v1/campaigns");
}

export function getCampaign(campaignId: number): Promise<CampaignSummary> {
  return apiFetch<CampaignSummary>(`/api/v1/campaigns/${campaignId}`);
}

export function listRecipients(campaignId: number): Promise<CampaignRecipient[]> {
  return apiFetch<CampaignRecipient[]>(`/api/v1/campaigns/${campaignId}/recipients`);
}

/* --- Reparto de leads --- */

export function getAssignmentRule(): Promise<AssignmentRule> {
  return apiFetch<AssignmentRule>("/api/v1/assignment");
}

/* --- Documentos --- */

export function listDocuments(agentId?: number): Promise<AgentDocument[]> {
  return apiFetch<AgentDocument[]>(`/api/v1/documents${searchSuffix({ agent_id: agentId })}`);
}

export function getDocumentFormats(): Promise<DocumentFormats> {
  return apiFetch<DocumentFormats>("/api/v1/documents/formats");
}
